import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Loader,
  Menu,
  Stack,
  Table,
  Text,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconDownload,
  IconRefresh,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';

import type { AttachmentViewerState } from './useUploadAttachmentViewer';

import { getOutlineControlProps } from 'utils/buttonUtils';
import { getDisplayNameFromStoredAttachmentName } from 'utils/intakeAttachmentKey';

interface UploadAttachmentViewerProps {
  viewer: AttachmentViewerState;
  isCompact: boolean;
  pdfHeight: string;
  activeAttachmentName?: string;
  activeAttachmentConfidenceLabel?: string | null;
}

export const UploadAttachmentViewer: React.FC<UploadAttachmentViewerProps> = ({
  viewer,
  pdfHeight,
  activeAttachmentName,
  activeAttachmentConfidenceLabel,
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');
  const {
    fileIndex,
    fileKeys,
    fileLoading,
    fileError,
    fileUrl,
    listLoading,
    listError,
    zoom,
    csvPreview,
    excelPreview,
    previewType,
    canPrev,
    canNext,
    goPrev,
    goNext,
    zoomIn,
    zoomOut,
    resetZoom,
    handleImageDoubleClick,
    handleImageViewportRef,
    handleImageLoad,
    downloadCurrent,
    downloadAll,
    downloadCurrentLoading,
    downloadAllLoading,
    downloadError,
  } = viewer;
  const displayAttachmentName = activeAttachmentName
    ? getDisplayNameFromStoredAttachmentName(activeAttachmentName)
    : '';

  const downloadDisabled =
    !fileKeys.length || listLoading || fileLoading || downloadAllLoading;
  const downloadAllDisabled =
    fileKeys.length <= 1 ||
    !fileKeys.length ||
    listLoading ||
    downloadCurrentLoading ||
    downloadAllLoading;
  const prevDisabled = !canPrev;
  const nextDisabled = !canNext;
  const showImage = previewType === 'image';
  const showPdf = previewType === 'pdf';
  const showCsv = previewType === 'csv';
  const showExcel = previewType === 'excel';
  const tabularPreview = showExcel ? excelPreview : csvPreview;
  const zoomDisabled = !showImage && !showPdf;
  const shouldCenterImage = zoom < 1;
  const tabularRowSummary = tabularPreview
    ? tabularPreview.truncatedRows
      ? translate(
          'pages:uploads.uploadsModal.csvPreviewRowsFirst',
          { count: tabularPreview.rows.length },
          `first ${tabularPreview.rows.length} rows`
        )
      : translate(
          'pages:uploads.uploadsModal.csvPreviewRowsAll',
          { count: tabularPreview.rows.length },
          `${tabularPreview.rows.length} rows`
        )
    : '';
  const tabularColumnSummary = tabularPreview
    ? tabularPreview.truncatedColumns
      ? translate(
          'pages:uploads.uploadsModal.csvPreviewColumnsFirst',
          { count: tabularPreview.columns.length },
          `first ${tabularPreview.columns.length} columns`
        )
      : translate(
          'pages:uploads.uploadsModal.csvPreviewColumnsAll',
          { count: tabularPreview.columns.length },
          `all ${tabularPreview.columns.length} columns`
        )
    : '';
  const tabularPreviewSummary = tabularPreview
    ? translate(
        'pages:uploads.uploadsModal.csvPreviewSummary',
        {
          rowSummary: tabularRowSummary,
          columnSummary: tabularColumnSummary,
        },
        `Showing ${tabularRowSummary} and ${tabularColumnSummary}.`
      )
    : null;
  const tabularPreviewLimitMessage =
    tabularPreview &&
    (tabularPreview.truncatedRows || tabularPreview.truncatedColumns)
      ? translate(
          tabularPreview.truncatedRows && tabularPreview.truncatedColumns
            ? 'pages:uploads.uploadsModal.csvPreviewTruncatedBoth'
            : tabularPreview.truncatedRows
            ? 'pages:uploads.uploadsModal.csvPreviewTruncatedRows'
            : 'pages:uploads.uploadsModal.csvPreviewTruncatedColumns',
          {
            rowCount: tabularPreview.rows.length,
            columnCount: tabularPreview.columns.length,
          },
          tabularPreview.truncatedRows && tabularPreview.truncatedColumns
            ? `Preview limited to first ${tabularPreview.rows.length} rows and first ${tabularPreview.columns.length} columns.`
            : tabularPreview.truncatedRows
            ? `Preview limited to first ${tabularPreview.rows.length} rows.`
            : `Preview limited to first ${tabularPreview.columns.length} columns.`
        )
      : null;
  const excelSheetSummary = excelPreview
    ? translate(
        'pages:uploads.uploadsModal.excelPreviewSheet',
        { sheetName: excelPreview.sheetName },
        `Previewing first worksheet: ${excelPreview.sheetName}.`
      )
    : null;
  const downloadErrorMessage =
    downloadError === 'multiple'
      ? translate(
          'pages:uploads.uploadsModal.downloadAllFailed',
          'Failed to download one or more attachments.'
        )
      : translate(
          'pages:uploads.uploadsModal.downloadFailed',
          'Failed to download attachment.'
        );

  const renderTabularPreview = (
    preview: NonNullable<typeof tabularPreview>,
    options?: {
      sheetSummary?: string | null;
      emptyMessage?: string;
      testId?: string;
    }
  ) => {
    const {
      sheetSummary = null,
      emptyMessage = 'CSV file is empty.',
      testId = 'csv-preview-table',
    } = options || {};

    return (
      <Flex direction="column" h="100%" gap="xs">
        <Stack gap={2} px="xs" pt="xs">
          {sheetSummary ? (
            <Text size="xs" c="dimmed">
              {sheetSummary}
            </Text>
          ) : null}
          <Group justify="space-between" align="flex-start">
            <Text size="xs" c="dimmed">
              {tabularPreviewSummary}
            </Text>
            {tabularPreviewLimitMessage ? (
              <Text size="xs" c="dimmed">
                {tabularPreviewLimitMessage}
              </Text>
            ) : null}
          </Group>
          {sheetSummary ? <Divider /> : null}
        </Stack>
        {preview.columns.length > 0 ? (
          <Box style={{ flex: 1, minHeight: 0 }}>
            <Table.ScrollContainer
              minWidth={Math.max(480, preview.columns.length * 140)}
              style={{ height: '100%' }}
            >
              <Table
                data-testid={testId}
                stickyHeader
                withTableBorder
                withColumnBorders
                highlightOnHover
                horizontalSpacing="xs"
                verticalSpacing="xs"
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    {preview.columns.map((column, index) => (
                      <Table.Th key={`${column}-${index}`}>{column}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {preview.rows.map((row, rowIndex) => (
                    <Table.Tr key={`${testId}-row-${rowIndex}`}>
                      <Table.Td>{rowIndex + 1}</Table.Td>
                      {row.map((value, columnIndex) => (
                        <Table.Td
                          key={`${testId}-cell-${rowIndex}-${columnIndex}`}
                        >
                          {value}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Box>
        ) : (
          <Flex align="center" justify="center" h="100%">
            <Text size="sm" c="dimmed">
              {emptyMessage}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  };

  const renderAttachmentContent = () => {
    const isLoading = listLoading || fileLoading;

    return (
      <>
        {isLoading && (
          <Flex align="center" justify="center" h="100%">
            <Loader />
          </Flex>
        )}

        {!isLoading && listError && (
          <Flex align="center" justify="center" h="100%">
            <Text size="sm" c="red">
              {listError}
            </Text>
          </Flex>
        )}

        {!isLoading && !listError && fileKeys.length === 0 && (
          <Flex align="center" justify="center" h="100%">
            <Text size="sm" c="dimmed">
              {translate(
                'pages:uploads.uploadsModal.noAttachments',
                'No attachments found for this upload.'
              )}
            </Text>
          </Flex>
        )}

        {!isLoading && !listError && fileKeys.length > 0 && (
          <Box
            style={{
              width: '100%',
              minWidth: 0,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {fileError && (
              <Flex align="center" justify="center" h="100%">
                <Text size="sm" c="red">
                  {fileError}
                </Text>
              </Flex>
            )}
            {!fileError && fileUrl && showImage && (
              <Box
                ref={handleImageViewportRef}
                style={{
                  height: '100%',
                  width: '100%',
                  overflow: 'auto',
                }}
              >
                <Box
                  data-testid="image-zoom-canvas"
                  style={{
                    display: 'flex',
                    justifyContent: shouldCenterImage ? 'center' : 'flex-start',
                    alignItems: 'flex-start',
                    width: '100%',
                    minWidth: 0,
                    padding: 12,
                    boxSizing: 'border-box',
                  }}
                >
                  <img
                    src={fileUrl}
                    alt={translate(
                      'pages:uploads.uploadsModal.attachmentAlt',
                      'Upload attachment'
                    )}
                    style={{
                      width: `${zoom * 100}%`,
                      maxWidth: 'none',
                      height: 'auto',
                      display: 'block',
                      cursor: zoom < 3 ? 'zoom-in' : 'default',
                    }}
                    onLoad={(event) =>
                      handleImageLoad(
                        event.currentTarget.naturalWidth,
                        event.currentTarget.naturalHeight
                      )
                    }
                    onDoubleClick={handleImageDoubleClick}
                  />
                </Box>
              </Box>
            )}
            {!fileError && fileUrl && showPdf && (
              <Box
                ref={handleImageViewportRef}
                data-testid="pdf-zoom-viewport"
                style={{
                  width: '100%',
                  height: pdfHeight,
                  overflow: 'auto',
                }}
              >
                <Box
                  data-testid="pdf-zoom-canvas"
                  style={{
                    width: `${zoom * 100}%`,
                    height: `${zoom * 100}%`,
                    marginInline: zoom < 1 ? 'auto' : undefined,
                  }}
                >
                  <iframe
                    src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                    title={translate(
                      'pages:uploads.uploadsModal.attachmentAlt',
                      'Upload attachment'
                    )}
                    style={{
                      border: 'none',
                      width: `${100 / zoom}%`,
                      height: `${100 / zoom}%`,
                      transform: `scale(${zoom})`,
                      transformOrigin: 'top left',
                    }}
                  />
                </Box>
              </Box>
            )}
            {!fileError &&
              fileUrl &&
              showCsv &&
              csvPreview &&
              renderTabularPreview(csvPreview, {
                emptyMessage: translate(
                  'pages:uploads.uploadsModal.csvPreviewEmpty',
                  'CSV file is empty.'
                ),
                testId: 'csv-preview-table',
              })}
            {!fileError &&
              fileUrl &&
              showExcel &&
              excelPreview &&
              renderTabularPreview(excelPreview, {
                sheetSummary: excelSheetSummary,
                emptyMessage: translate(
                  'pages:uploads.uploadsModal.excelPreviewEmpty',
                  'Excel worksheet is empty.'
                ),
                testId: 'excel-preview-table',
              })}
            {!fileError &&
              fileUrl &&
              !showImage &&
              !showPdf &&
              !showCsv &&
              !showExcel && (
                <Flex align="center" justify="center" h="100%">
                  <Text size="sm" c="dimmed">
                    {translate(
                      'pages:uploads.uploadsModal.previewUnavailable',
                      'Preview not available for this file type.'
                    )}
                  </Text>
                </Flex>
              )}
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <Group justify="space-between" mb="sm" align="center">
        <Group gap="xs">
          <Button
            size="xs"
            leftSection={<IconChevronLeft size={14} />}
            disabled={prevDisabled}
            onClick={goPrev}
            {...getOutlineControlProps(computedColorScheme, prevDisabled)}
          >
            {translate('pages:uploads.uploadsModal.prev', 'Prev')}
          </Button>
          <Button
            size="xs"
            rightSection={<IconChevronRight size={14} />}
            disabled={nextDisabled}
            onClick={goNext}
            {...getOutlineControlProps(computedColorScheme, nextDisabled)}
          >
            {translate('pages:uploads.uploadsModal.next', 'Next')}
          </Button>
          {fileKeys.length > 0 && (
            <Text size="sm" c="dimmed">
              {fileIndex + 1} / {fileKeys.length}
            </Text>
          )}
          <Group wrap="nowrap" gap={0}>
            <Button
              size="xs"
              leftSection={<IconDownload size={14} />}
              onClick={downloadCurrent}
              loading={downloadCurrentLoading}
              disabled={downloadDisabled}
              {...getOutlineControlProps(
                computedColorScheme,
                downloadDisabled,
                {
                  borderTopRightRadius: 2,
                  borderBottomRightRadius: 2,
                }
              )}
            >
              {translate('buttons.download', 'Download')}
            </Button>
            <Menu
              transitionProps={{ transition: 'pop' }}
              position="bottom-end"
              withinPortal
              disabled={downloadAllDisabled}
            >
              <Menu.Target>
                <ActionIcon
                  size={30}
                  aria-label={translate('buttons.downloadAll', 'Download All')}
                  {...getOutlineControlProps(
                    computedColorScheme,
                    downloadAllDisabled,
                    {
                      borderTopLeftRadius: 2,
                      borderBottomLeftRadius: 2,
                    }
                  )}
                >
                  <IconChevronDown size={16} stroke={1.5} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={downloadAll}
                  leftSection={<IconDownload size={14} />}
                >
                  {translate('buttons.downloadAll', 'Download All')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        <Group gap="xs">
          <Tooltip
            label={translate('pages:uploads.uploadsModal.zoomOut', 'Zoom out')}
          >
            <ActionIcon
              aria-label={translate(
                'pages:uploads.uploadsModal.zoomOut',
                'Zoom out'
              )}
              onClick={zoomOut}
              disabled={zoomDisabled}
              {...getOutlineControlProps(computedColorScheme, zoomDisabled)}
            >
              <IconZoomOut size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={translate(
              'pages:uploads.uploadsModal.zoomReset',
              'Reset zoom'
            )}
          >
            <ActionIcon
              aria-label={translate(
                'pages:uploads.uploadsModal.zoomReset',
                'Reset zoom'
              )}
              onClick={resetZoom}
              disabled={zoomDisabled}
              {...getOutlineControlProps(computedColorScheme, zoomDisabled)}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={translate('pages:uploads.uploadsModal.zoomIn', 'Zoom in')}
          >
            <ActionIcon
              aria-label={translate(
                'pages:uploads.uploadsModal.zoomIn',
                'Zoom in'
              )}
              onClick={zoomIn}
              disabled={zoomDisabled}
              {...getOutlineControlProps(computedColorScheme, zoomDisabled)}
            >
              <IconZoomIn size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {(displayAttachmentName || activeAttachmentConfidenceLabel) && (
        <Group justify="space-between" mb="sm" gap="xs" wrap="wrap">
          {displayAttachmentName ? (
            <Group gap="xs" wrap="wrap">
              <Text size="sm" fw={500} data-testid="upload-attachment-name">
                {displayAttachmentName}
              </Text>
            </Group>
          ) : (
            <span />
          )}
          {activeAttachmentConfidenceLabel ? (
            <Text
              size="sm"
              c="dimmed"
              data-testid="upload-attachment-confidence"
            >
              {activeAttachmentConfidenceLabel}
            </Text>
          ) : null}
        </Group>
      )}
      {downloadError && (
        <Text size="sm" c="red" mb="sm">
          {downloadErrorMessage}
        </Text>
      )}

      {renderAttachmentContent()}
    </>
  );
};
